import { useState, Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { SidebarGroupProps } from '@type/filters';
import { useTranslation } from 'next-i18next';
import { Button } from '@components/common';
import { SidebarHeader } from '@components/features/filters/partials/SidebarHeader';
import { cn } from '@root/src/utils/cn';


/**
 * @description The sidebar filters group component. Wraps the filters in a group with a title and delete action.
 * * **title** - the title of the group.
 * * **activeFilters** - the active filters in the group.
 * * **emptyLabel** - the label when the group has not active filters.
 * * **submitFunc** - the submit action for the group. Used if you want to submit the form when a group is reset.
 * * **clearGroup** - the delete action for the group.
 * * **showDetails** - the state for showing the details of the group.
 * * **setShowDetails** - the state setter for showing the details of the group.
 */
export const SidebarGroup = (props: SidebarGroupProps) => {
  // Translation
  const { t } = useTranslation('common');

  // Destructure props
  const {
    title,
    activeFilters,
    emptyLabel = t('global.action.choose'),
    submitFunc,
    clearGroup,
    showDetails,
    setShowDetails,
    children,
    arrowClassName,
    buttonClassName,
    onDetailsToggle,
    isActive,
    ...restProps
  } = props;

  // Show details for filter groups
  const [showThisDetails, setShowThisDetails] = useState(false);

  // If this group becomes active from outside, open its details view
  useEffect(() => {
    if (!isActive) return;
    setShowDetails(true);
    setShowThisDetails(true);
    onDetailsToggle?.(true);
  }, [isActive, onDetailsToggle, setShowDetails]);


  // Render component
  // ***********************************************
  return (
    <>
      {/* LIST ELEMENT ********************************* */}
      <Transition
        as={Fragment}
        show={!showDetails}
        enter="transform transition ease-in-out duration-200"
        enterFrom="-translate-x-full"
        enterTo="translate-x-0"
        leave="transform transition ease-in-out duration-200"
        leaveFrom="translate-x-0"
        leaveTo="-translate-x-full"
        unmount={false}
        {...restProps}
      >
        <Button
          styling="none"
          className={cn('flex w-full items-center border-b border-b-surface-100 p-4 text-left hover:bg-surface-50', buttonClassName)}
          onClick={() => {
            setShowDetails(true);
            setShowThisDetails(true);
            if (onDetailsToggle) onDetailsToggle(true);
          }}
        >
          <div className="mr-2 min-w-0 flex-1">
            {/* Title in filters list */}
            <span className="block font-bold">{title}</span>

            { /* Display the list with the active filters or an empty label, if set */
              (activeFilters || emptyLabel) && (
                <span className="block text-sm text-ink-medium">{activeFilters || emptyLabel}</span>
              )
            }
          </div>
          <ChevronRightIcon className={cn(`size-5 shrink-0 text-ink-light hover:text-ink${showDetails ? ' !invisible' : ''}`, arrowClassName)} />
        </Button>
      </Transition>

      {/* FORM FILTERS ********************************* */}
      <Transition
        as={Fragment}
        show={showDetails}
        enter="transform transition ease-in-out duration-200"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transform transition ease-in-out duration-200"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
        afterLeave={() => setShowThisDetails(false)}
        unmount={false}
      >
        <div className={`absolute inset-0 overflow-y-auto bg-surface ${showThisDetails ? 'z-10' : '-z-10'}`}>

          {/* Header in form view */}
          <SidebarHeader
            title={title}
            hasBackButton
            backButtonClick={() => {
              // Save the form on returning to the list
              if (submitFunc) submitFunc();
              setShowDetails(false);
              if (onDetailsToggle) onDetailsToggle(false);
            }}
            hasDeleteButton={!!activeFilters}
            deleteButtonClick={() => {
              if (clearGroup) clearGroup();
              if (submitFunc) setTimeout(() => submitFunc(), 50);
              setShowDetails(false);
              if (onDetailsToggle) onDetailsToggle(false);
            }}
          />

          {/* Children */}
          <div className="w-full p-4 lg:p-5">
            {children}
          </div>

        </div>
      </Transition>
    </>
  );
};
